import {useCallback, useEffect, useRef, useState} from "react";
import {fileListRefusalOf, isAboveLargestPlanRefusal, type FileListRefusal} from "api/apiErrorIds";
import {heldPeerIdentity} from "core/p2p/heldPeerIdentity";
import {OverLimitApi} from "./OverLimitApi";
import type {FileListFacts} from "./fileListRules";
import {
	allowanceAfterRead,
	sameOverLimitAllowance,
	type OverLimitAllowance,
	type OverLimitOffer,
	type OverLimitPricing,
} from "./overLimitOffer";

/**
 * The allowance read, naming the peer id this tab holds when it holds one (kitecorp/transferify-api-service#2787):
 * the api lets the tab's older instant send, which the next instant registration stops serving, give its unused
 * purchase to the send being composed. `OverLimitApi.getAllowance` sends it on an instant read only.
 */
function readAllowance(bytes: number, pricing: OverLimitPricing) {
	const peerId = heldPeerIdentity()?.peerId;
	return peerId === undefined ? OverLimitApi.getAllowance(bytes, pricing)
		: OverLimitApi.getAllowance(bytes, pricing, peerId);
}

export interface OverLimitPurchaseVM {
	/** The server's quote for the current selection, or null while outstanding / never asked. */
	offer: OverLimitOffer | null;
	/** The account's settled purchase as last read, or null (none — the ordinary first answer). */
	allowance: OverLimitAllowance | null;
	/**
	 * The account has answered, on this delivery path and since this sender became the audience, whether
	 * it already holds a purchase: one was found, none was, or the read failed.
	 *
	 * What tells {@link allowance}'s `null` apart: "holds nothing" once this is true, "not asked yet"
	 * before. The two used to be one value, and a composer that came back after a refused upload start
	 * priced a send its CONFIRMED allowance covered whenever the quote won the race with this read
	 * (kitecorp/transferify-ui#3010).
	 */
	allowanceAnswered: boolean;
	/** True when the QUOTE gave up: asked, re-asked its whole budget, and never arrived. */
	quoteFailed: boolean;
	/**
	 * Opens the payment dialog on the plans (kitecorp/transferify-api-service#3005, Q5; kitecorp/transferify-ui#3800):
	 * no wall sells a one-off, so there is no price to hold and no session to mint here, and {@link paymentOpen} is
	 * true until {@link dismissPayment}. The plan's own purchase is `useOverLimitPlanPurchase`'s, which opens with the
	 * dialog.
	 */
	startPlanChoice: () => void;
	/** Whether the payment dialog is on screen. */
	paymentOpen: boolean;
	/** Close the dialog. The selection survives it, and so does a settled allowance. */
	dismissPayment: () => void;
	/** Re-ask a quote that gave up — the action the `quoteFailed` surface offers. */
	retryQuote: () => void;
	/**
	 * How many quote asks have been settled by an answer: an offer, a file-list refusal, an above-largest-plan refusal or a
	 * failure after the last retry. A caller that asks again and must know the new answer has landed saves this when it
	 * asks and waits for it to move; the offer itself cannot say, because it is cleared and set again inside one batch
	 * when the answer comes at once. An ask that is superseded or retried settles nothing.
	 */
	quoteAnswers: number;
	/**
	 * The file-list rule the api refused this selection's quote under, or null (kitecorp/transferify-ui#2930, api
	 * kitecorp/transferify-api-service#2144): the upload init would refuse the list whatever was paid. An ANSWER, not
	 * an outage, so it is never re-asked and never reported as a failed quote; it lasts until the selection's facts
	 * change or the audience goes away.
	 */
	fileListRefusal: FileListRefusal | null;
	/**
	 * The api refused this size's quote because no plan holds it (kitecorp/transferify-api-service#3005). An ANSWER,
	 * not an outage: never re-asked, never a failed quote; it lasts until the size changes or the audience goes away.
	 */
	aboveLargestPlan: boolean;
}

export interface OverLimitPurchaseInput {
	/**
	 * The sender is signed in and over their FREE cap — the only audience with anything to pay. True on
	 * EITHER delivery path since ui#1719; it named the hosted route while that was the only path with a
	 * price. False for every subscribed tier, which is one of the three places
	 * {@link ./overLimitOffer.resolveOverLimit} keeps a subscriber away from this checkout.
	 */
	active: boolean;
	/** The total selected bytes. The quote is per SIZE, so this is what the offer is keyed on. */
	bytes: number;
	/**
	 * Which rule prices this send ({@link ../components/createFormLogic.overLimitPricingOf}).
	 *
	 * Threaded through BOTH calls (api#1039): the purchase read asks for this path because what a purchase pays for
	 * depends on it (a purchase pays for either type for what was paid, kitecorp/transferify-api-service#2787). It is
	 * also a quote DEPENDENCY: changing the composer from hosted to instant changes the answer, and a quote held
	 * beside the new path is the other path's.
	 */
	pricing: OverLimitPricing;
	/**
	 * The selection's file-list facts ({@link ./fileListRules.fileListFactsOf}), declared on the quote so the api
	 * refuses a list the upload init would refuse before any plan is offered (api kitecorp/transferify-api-service#2144).
	 * A quote DEPENDENCY like `bytes`: a different list is a different question, and a refusal belongs to the list it
	 * was given for.
	 */
	files: FileListFacts;
}

/**
 * How many times a failed quote re-asks itself before the sender is asked to.
 *
 * Exported so the tests spend the same budget the hook does rather than restating the number, which is
 * how a test keeps passing after the constant it was written against changed.
 */
export const QUOTE_RETRIES = 3;

/**
 * The composer's over-cap answers (ui#1454, api#823): the quote for the current selection, whose plan rows the
 * walls offer, the settled allowance the upload then names, and whether the payment dialog is open. No one-off is
 * bought here any more (kitecorp/transferify-ui#3916); the plan's purchase is `useOverLimitPlanPurchase`'s.
 *
 * All DECISIONS live in the React-free `overLimitOffer` rules; this hook only schedules them.
 */
export function useOverLimitPurchase(
	{active, bytes, pricing, files}: OverLimitPurchaseInput,
): OverLimitPurchaseVM {
	// The facts as the primitives they are, so a list rebuilt with the same values is the same question and a
	// render that recreated the object does not re-ask the quote.
	const {fileCount, longestFileNameLength, longestContentTypeLength} = files;
	const [offer, setOffer] = useState<OverLimitOffer | null>(null);
	const [fileListRefusal, setFileListRefusal] = useState<FileListRefusal | null>(null);
	const [aboveLargestPlan, setAboveLargestPlan] = useState(false);
	const [allowance, setAllowance] = useState<OverLimitAllowance | null>(null);
	// Which delivery path the account has answered the purchase question for in this arm, or null
	// before it has. Keyed on the PATH rather than a boolean because the api answers per path (api#1039):
	// a hosted "nothing" is no answer about an instant purchase. See `allowanceAnswered`.
	const [allowanceAnsweredFor, setAllowanceAnsweredFor] = useState<OverLimitPricing | null>(null);
	const [quoteFailed, setQuoteFailed] = useState(false);
	// The dialog is open on the plans (see {@link startPlanChoice}).
	const [planChoiceOpen, setPlanChoiceOpen] = useState(false);
	// The re-ask budget belongs to the REQUEST, not to the hook's lifetime: a changed size or route
	// starts with a full one, so a sender who simply added a file is never failed on the budget the
	// previous quote spent. A ref because spending it must not itself cause a render.
	const quoteRetriesLeft = useRef(QUOTE_RETRIES);
	// Which request the budget above belongs to, so a re-ask can be told from a new ask.
	const quotedFor = useRef("");
	// Bumping this re-runs the quote effect: it is how a failed or SUPERSEDED quote is re-asked without
	// the size having changed.
	const [attempt, setAttempt] = useState(0);
	const [quoteAnswers, setQuoteAnswers] = useState(0);
	// The latest size, for the read that must NOT re-arm on every added file (see below).
	const bytesRef = useRef(bytes);
	bytesRef.current = bytes;

	/**
	 * Records what a read for `askedBytes` found.
	 *
	 * Goes through {@link allowanceAfterRead} rather than adopting the answer as-is, because the api
	 * filters `GET /allowance?bytes=` BY SIZE: "nothing" at a LARGER size is not evidence about the
	 * purchase in hand, and dropping it there would strand a sender who added a file and removed it
	 * again. Object-stable, so a read that changes nothing does not re-render the panel (the ui#824 discipline).
	 */
	const adopt = useCallback((next: OverLimitAllowance | null, askedBytes: number) => {
		setAllowance(previous => {
			const held = allowanceAfterRead(previous, next, askedBytes);
			return sameOverLimitAllowance(previous, held) ? previous : held;
		});
	}, []);

	// The quote, re-asked whenever the SIZE changes — the plans it offers are a function of the size, so a
	// stale quote beside a grown selection offers plans that may not hold it. Cheap to re-ask: `/offer` is
	// the deployed catalogue plus the owner's rule, and touches no provider.
	//
	// Cleared when the audience goes away, so a sender who removes files back under the cap and then
	// adds different ones cannot be shown the previous selection's answer for a moment.
	useEffect(() => {
		if (!active) {
			setOffer(null);
			setQuoteFailed(false);
			// A refusal belongs to the ask it answered; the next audience's selection is asked afresh.
			setFileListRefusal(null);
			setAboveLargestPlan(false);
			// The next arm is a NEW ask and gets a full budget, not the remains of the one that was
			// spent before this audience went away.
			quotedFor.current = "";
			return;
		}
		const requested = `${bytes}:${pricing}:`
			+ `${fileCount}:${longestFileNameLength}:${longestContentTypeLength}`;
		if (quotedFor.current !== requested) {
			quotedFor.current = requested;
			quoteRetriesLeft.current = QUOTE_RETRIES;
		}
		// CLEARED BEFORE THE REFETCH, not only when the audience goes away. A quote belongs to the size it
		// was asked for, and holding the previous one across a growing selection puts a stale answer beside a
		// LIVE CTA. Back to `quoting`, which has no CTA, until the answer lands.
		setOffer(null);
		// Back to `quoting` for the duration of this ask. A re-ask in flight is a PENDING quote, not a
		// failed one, and leaving the failure on screen underneath a live request tells the sender the
		// opposite of what is happening.
		setQuoteFailed(false);
		// And a refusal belongs to the list it was given for: a selection the sender has changed is a new question.
		setFileListRefusal(null);
		setAboveLargestPlan(false);
		let disposed = false;
		let retry: ReturnType<typeof setTimeout> | undefined;
		const declared = {fileCount, longestFileNameLength, longestContentTypeLength};
		void OverLimitApi.getOffer(bytes, pricing, declared)
			.then(next => {
				if (disposed) return;
				setOffer(next);
				setQuoteAnswers(count => count + 1);
			})
			.catch((error: unknown) => {
				// A failed quote is not "free" and not "unsellable" — leaving it null holds the surface on
				// `quoting`, which blocks the send rather than letting an unpriced one through. But
				// `quoting` offers no control, so re-ask a bounded number of times first: a single flaky
				// response must not cost a sender their purchase.
				//
				// AND THEN SAY SO. Returning silently once the budget was spent is what held the surface
				// on `quoting` for good — a spinner over a request that had already given up, with no
				// error and no retry (the 2026-08-30 prod defect, ui#1715). Every exit from here now
				// leaves a state the sender can act on.
				if (disposed) {
					// no-state: this ask was superseded or the composer unmounted. There is no surface
					// left to strand — whatever replaced it owns the state now, and writing one here
					// would put a dead request's verdict on a live selection's strip.
					return;
				}
				// A REFUSAL UNDER A FILE-LIST RULE IS AN ANSWER, not a failure (api#2144): the upload init would refuse
				// this list whatever was paid, so re-asking cannot change it and "we couldn't work out the price"
				// would be untrue. The rule is named instead, and no payment step follows.
				const refusal = fileListRefusalOf(error);
				if (refusal !== null) {
					setFileListRefusal(refusal);
					setQuoteAnswers(count => count + 1);
					return;
				}
				// An answer, not a failure: the api sells no plan and no payment above this size; a purchase that
				// already covers it is the allowance read's to find, and outranks this rung.
				if (isAboveLargestPlanRefusal(error)) {
					setAboveLargestPlan(true);
					setQuoteAnswers(count => count + 1);
					return;
				}
				if (quoteRetriesLeft.current <= 0) {
					setQuoteFailed(true);
					setQuoteAnswers(count => count + 1);
					return;
				}
				quoteRetriesLeft.current--;
				retry = setTimeout(() => setAttempt(previous => previous + 1), 2000);
			});
		return () => {
			disposed = true;
			if (retry !== undefined) clearTimeout(retry);
		};
		// `pricing` is a dependency for a stronger version of `bytes`' reason (api#1039): it selects the
		// RULE, not merely an input to it, so a quote held across a switch between hosted and instant is the
		// other rule's figure. The three file-list facts are dependencies for
		// `bytes`' reason (api#2144): the api judges the list the quote declares, so a changed list is re-asked.
	}, [active, bytes, pricing, attempt, fileCount, longestFileNameLength,
		longestContentTypeLength]);

	// "Does this account already hold a purchase" — read when the over-cap state ARMS, and again when the
	// delivery path changes.
	//
	// Deliberately NOT keyed on `bytes`, unlike the quote above. On a MISS the api runs its dead-session
	// heal, which costs a provider call, so re-reading per added file would spend one per keystroke-sized
	// selection change. Reading with the size at read time is sufficient because coverage is then
	// re-decided locally by `allowanceCovers`: a purchase covers every size at or below the one it was
	// quoted for, and a selection GROWN past it falls back to the wall.
	//
	// EVERY SETTLED READ RECORDS THAT THE ACCOUNT HAS ANSWERED (kitecorp/transferify-ui#3010), because the
	// rules decide nothing that a purchase would overturn until it has: a covering purchase outranks every
	// answer the quote can give.
	useEffect(() => {
		if (!active) {
			// The next arm has not been asked anything yet: its answer is its own.
			setAllowanceAnsweredFor(null);
			return;
		}
		let disposed = false;
		const askedBytes = bytesRef.current;
		void readAllowance(askedBytes, pricing)
			.then(existing => {
				if (disposed) return;
				adopt(existing, askedBytes);
				setAllowanceAnsweredFor(pricing);
			})
			.catch(() => {
				if (disposed) {
					// no-state: this read was superseded by a disarm or a switch of delivery path. The effect
					// run that replaced it asks again and records its own answer.
					return;
				}
				// A FAILED READ STILL ENDS THE WAIT. The rung that waits on it offers no action, so holding
				// it would strand the sender. The strip falls back to the wall, and the purchase is asked for
				// again on the next arm or switch of delivery path.
				setAllowanceAnsweredFor(pricing);
			});
		return () => {
			disposed = true;
		};
		// `pricing` re-reads deliberately, unlike `bytes`. The api answers `/allowance` PER DELIVERY PATH
		// (api#1039), so a hosted "nothing yet" is not evidence about an instant purchase — carrying it
		// across the switch would leave a sender who has paid looking at the wall.
	}, [active, pricing, adopt]);

	const dismissPayment = useCallback(() => setPlanChoiceOpen(false), []);

	const startPlanChoice = useCallback(() => setPlanChoiceOpen(true), []);

	// A fresh budget, not one more attempt: the sender pressing this has waited out the automatic
	// re-asks, and handing them a single try would fail again on the next blip with nothing gained.
	const retryQuote = useCallback(() => {
		quoteRetriesLeft.current = QUOTE_RETRIES;
		setQuoteFailed(false);
		setAttempt(previous => previous + 1);
	}, []);

	return {
		offer, allowance,
		// Derived rather than stored as a boolean, so a disarm or a switch of path can never leave an
		// earlier arm's answer standing in for this one.
		allowanceAnswered: active && allowanceAnsweredFor === pricing,
		quoteFailed,
		startPlanChoice, paymentOpen: planChoiceOpen, dismissPayment, retryQuote, quoteAnswers,
		fileListRefusal, aboveLargestPlan,
	};
}
